/**
 * TEAM ACCESS CONTROL
 *
 * High-level access control checks for common scenarios.
 * Uses role-helpers.ts for role logic.
 */

import type { UserRole } from '@/types/database';
import { hasTeamAccess, getAdminTeam, isAdmin } from './role-helpers';

/**
 * Check if user can access IT tasks and functionality
 * @param role User role
 * @returns true if user can access IT work
 */
export function canAccessITWork(role: UserRole): boolean {
  return hasTeamAccess(role, 'IT');
}

/**
 * Check if user can access Data tasks and functionality
 * @param role User role
 * @returns true if user can access Data work
 */
export function canAccessDataWork(role: UserRole): boolean {
  return hasTeamAccess(role, 'DATA');
}

/**
 * Check if user can access Platform tasks and functionality
 * @param role User role
 * @returns true if user can access Platform work
 */
export function canAccessPlatformWork(role: UserRole): boolean {
  return hasTeamAccess(role, 'PLATFORM');
}

/**
 * Check if user can manage users
 * @param role User role
 * @returns true if user can manage users
 */
export function canManageUsers(role: UserRole): boolean {
  return isAdmin(role);
}

/**
 * Check if user can access user management for a specific team
 * SYSTEM_ADMIN can see all users
 * Team admins can see only their team's users
 * Regular users cannot see user management
 * @param userRole Role of the user trying to access user management
 * @param targetTeam Team to check access for (optional, for team-specific checks)
 * @returns true if user can access user management
 */
export function canAccessUserManagement(userRole: UserRole, targetTeam?: 'PLATFORM' | 'IT' | 'DATA'): boolean {
  if (!isAdmin(userRole)) {
    return false;
  }

  // SYSTEM_ADMIN can access all user management
  if (userRole === 'SYSTEM_ADMIN') {
    return true;
  }

  // Team admins can access user management for their team
  if (targetTeam) {
    const adminTeam = getAdminTeam(userRole);
    return adminTeam === targetTeam;
  }

  // If no specific team check, allow team admins to access their own user management
  return true;
}

/**
 * Get the team a user can manage (if any)
 * @param role User role
 * @returns Team name if user is a team admin, null if SYSTEM_ADMIN or regular user
 */
export function getManagedTeam(role: UserRole): 'PLATFORM' | 'IT' | 'DATA' | null {
  return getAdminTeam(role);
}

/**
 * Check if user should see a specific sidebar item
 * @param role User role
 * @param itemRequiredRole Required role for this item (can be a single role or array)
 * @returns true if user should see this item
 */
export function shouldShowSidebarItem(role: UserRole, itemRequiredRole: UserRole | UserRole[]): boolean {
  // SYSTEM_ADMIN sees everything
  if (role === 'SYSTEM_ADMIN') {
    return true;
  }

  const required = Array.isArray(itemRequiredRole) ? itemRequiredRole : [itemRequiredRole];
  return required.includes(role);
}

/**
 * Determine where a user should be redirected on login
 * @param role User role
 * @returns Path to redirect to
 */
export function getDefaultRedirectPath(role: UserRole): string {
  switch (role) {
    case 'IT_TEAM':
    case 'IT_ADMIN':
      return '/it-tasks';
    case 'DATA_TEAM':
    case 'DATA_ADMIN':
      return '/data-tasks';
    case 'PLATFORM_TEAM':
    case 'PLATFORM_ADMIN':
      return '/home';
    case 'SYSTEM_ADMIN':
      return '/home';
  }
}

/**
 * Get the page title based on user role
 * @param role User role
 * @returns Human-readable role title
 */
export function getRoleTitle(role: UserRole): string {
  switch (role) {
    case 'SYSTEM_ADMIN':
      return 'System Admin';
    case 'PLATFORM_TEAM':
      return 'Platform Team Member';
    case 'PLATFORM_ADMIN':
      return 'Platform Admin';
    case 'IT_TEAM':
      return 'IT Team Member';
    case 'IT_ADMIN':
      return 'IT Admin';
    case 'DATA_TEAM':
      return 'Data Team Member';
    case 'DATA_ADMIN':
      return 'Data Admin';
  }
}

/**
 * Check if role change requires attention
 * Certain role changes might need special handling
 * @param oldRole Previous role
 * @param newRole New role
 * @returns true if this is a significant role change
 */
export function isSignificantRoleChange(oldRole: UserRole, newRole: UserRole): boolean {
  // Changing to/from admin is significant
  const wasAdmin = isAdmin(oldRole);
  const isNowAdmin = isAdmin(newRole);
  return wasAdmin !== isNowAdmin;
}

/**
 * Get filter options for user list based on current user role
 * @param role Current user role
 * @returns Object with roles that can be filtered
 */
export function getFilterableRoles(role: UserRole): { roles: UserRole[]; canFilterByTeam: boolean } {
  if (role === 'SYSTEM_ADMIN') {
    return {
      roles: ['SYSTEM_ADMIN', 'PLATFORM_TEAM', 'PLATFORM_ADMIN', 'IT_TEAM', 'IT_ADMIN', 'DATA_TEAM', 'DATA_ADMIN'],
      canFilterByTeam: true,
    };
  }

  if (role === 'IT_ADMIN') {
    return {
      roles: ['IT_TEAM'],
      canFilterByTeam: false,
    };
  }

  if (role === 'DATA_ADMIN') {
    return {
      roles: ['DATA_TEAM'],
      canFilterByTeam: false,
    };
  }

  if (role === 'PLATFORM_ADMIN') {
    return {
      roles: ['PLATFORM_TEAM'],
      canFilterByTeam: false,
    };
  }

  // Regular team members can't filter
  return {
    roles: [],
    canFilterByTeam: false,
  };
}
